import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import StyleChat from './StyleChat';
import { MessageSquare, ChevronRight, Send, Search } from 'lucide-react';

const SupportMessages = () => {
    const [tickets, setTickets] = useState([]);
    const [filteredTickets, setFilteredTickets] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);

    // Filter states
    const [filters, setFilters] = useState({
        status: 'all',
        priority: 'all',
        startDate: '',
        endDate: '',
        userEmail: '',
    });

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [selectedTicket]);

    useEffect(() => {
        applyFilters();
    }, [tickets, filters]);

    const fetchTickets = async () => {
        try {
            const response = await axios.get('https://allinoneai-d1a5a1b54c64.herokuapp.com/support-messages');
            const reversedTickets = response.data.reverse(); // Reverse the order of tickets
            setTickets(reversedTickets);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching tickets:', error);
            setLoading(false);
        }
    };


    const applyFilters = () => {
        let filtered = [...tickets];

        // Status filter
        if (filters.status !== 'all') {
            filtered = filtered.filter(ticket => ticket.ticketStatus === filters.status);
        }

        // Priority filter
        if (filters.priority !== 'all') {
            filtered = filtered.filter(ticket => ticket.priority === filters.priority);
        }

        // Date range filter
        if (filters.startDate && filters.endDate) {
            filtered = filtered.filter(ticket => {
                const ticketDate = new Date(ticket.createdAt);
                const start = new Date(filters.startDate);
                const end = new Date(filters.endDate);
                return ticketDate >= start && ticketDate <= end;
            });
        }

        // User email filter
        if (filters.userEmail) {
            filtered = filtered.filter(ticket =>
                ticket.ticketUserEmail.toLowerCase().includes(filters.userEmail.toLowerCase())
            );
        }

        setFilteredTickets(filtered);
    };

    const handleTicketSelect = async (ticketId) => {
        const ticket = tickets.find(t => t._id === ticketId);
        setSelectedTicket(ticket);
    };

    const handleStatusChange = async (newStatus) => {
        if (!selectedTicket) return;

        try {
            await axios.put(`https://allinoneai-d1a5a1b54c64.herokuapp.com/support-message-status/${selectedTicket._id}`, {
                ticketStatus: newStatus,
                adminMessage: selectedTicket.adminMessage
            });

            const updatedTicket = { ...selectedTicket, ticketStatus: newStatus };
            setSelectedTicket(updatedTicket);
            setTickets(tickets.map(ticket =>
                ticket._id === selectedTicket._id ? updatedTicket : ticket
            ));
        } catch (error) {
            console.error('Error updating ticket status:', error);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedTicket || sending) return;

        setSending(true);
        
        try {
            const updatedMessages = [...(selectedTicket.adminMessage || []), {
                message: newMessage,
                timestamp: new Date().toISOString(),
                sender: 'admin'
            }];

            await axios.put(`https://allinoneai-d1a5a1b54c64.herokuapp.com/support-message-status/${selectedTicket._id}`, {
                ticketStatus: selectedTicket.ticketStatus,
                adminMessage: updatedMessages
            });

            setSelectedTicket({
                ...selectedTicket,
                adminMessage: updatedMessages
            });

            setTickets(tickets.map(ticket =>
                ticket._id === selectedTicket._id
                    ? { ...ticket, adminMessage: updatedMessages }
                    : ticket
            ));

            setNewMessage('');
            setTimeout(scrollToBottom, 100);
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    const FilterSection = () => (
        <div className="card-header bg-transparent py-3 border-bottom">
            <div className="row g-3">
                <div className="col-md-3">
                    <select
                        className="form-select"
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                        <option value="all">All Status</option>
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                    </select>
                </div>
               
                <div className="col-md-3">
                    <input
                        type="date"
                        className="form-control"
                        value={filters.startDate}
                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    />
                </div>
                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        value={filters.endDate}
                        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    />
                </div>
                <div className="col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by user email..."
                        value={filters.userEmail}
                        onChange={(e) => setFilters({ ...filters, userEmail: e.target.value })}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h3 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <MessageSquare size={24} />
                        Support Messages
                    </h3>
                    <div className="d-flex align-items-center gap-2 small text-white-50">
                        <span>Admin</span>
                        <ChevronRight size={14} />
                        <span className="text-primary">Support Messages</span>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm border-0">
                <FilterSection />
                <div className="card-body p-0">
                    <div className="row g-0">
                        {/* Tickets Sidebar */}
                        <div className="col-lg-4 border-end" style={{ maxHeight: '80vh' }}>
                            <div className="p-3 border-bottom">
                                <h6 className="mb-0 fw-bold">Support Tickets</h6>
                            </div>
                            <div className="overflow-auto" style={{ height: 'calc(80vh - 57px - 89px)' }}>
                                {filteredTickets.map((ticket) => (
                                    <div
                                        key={ticket._id}
                                        onClick={() => handleTicketSelect(ticket._id)}
                                        className={`border-bottom transition-all p-3 ${selectedTicket?._id === ticket._id ? 'bg-secondary bg-opacity-25' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start gap-3">
                                            <div>
                                                <h6 className="mb-1 text-white">{ticket.subject}</h6>
                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                        <span className={`badge ${ticket.ticketStatus === 'in-progress' ? 'bg-warning' :
                                                            ticket.ticketStatus === 'resolved' ? 'bg-success' :
                                                                ticket.ticketStatus === 'closed' ? 'bg-secondary' :
                                                                    'bg-info'
                                                            }`}>
                                                            {ticket.ticketStatus}
                                                        </span>
                                                        {ticket.priority && (
                                                            <span className={`badge ${ticket.priority === 'high' ? 'bg-danger' :
                                                                ticket.priority === 'medium' ? 'bg-warning' :
                                                                    'bg-info'
                                                                }`}>
                                                                {ticket.priority}
                                                            </span>
                                                        )}
                                                        <small className="badge bg-info text-white">{ticket.ticketUserName}</small>
                                                        <small className="text-white-50">{ticket.ticketUserEmail}</small>
                                                    </div>
                                                </div>
                                                <small className="text-white-50">
                                                    {new Date(ticket.createdAt).toLocaleDateString()}
                                                </small>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>

                        {/* Chat Area */}
                        <div className="col-lg-8 d-flex flex-column" style={{ maxHeight: '80vh' }}>
                            {selectedTicket ? (
                                <>
                                    {/* Chat Header with Admin Controls */}
                                    <div className="p-3 border-bottom">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h6 className="mb-0 fw-bold">{selectedTicket.subject}</h6>
                                            <div className="d-flex gap-2">
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={selectedTicket.ticketStatus}
                                                    onChange={(e) => handleStatusChange(e.target.value)}
                                                    style={{ width: 'auto' }}
                                                >
                                                    <option value="open">Open</option>
                                                    <option value="in-progress">In Progress</option>
                                                    <option value="resolved">Resolved</option>
                                                    <option value="closed">Closed</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className={`badge ${selectedTicket.ticketStatus === 'in-progress' ? 'bg-warning' :
                                                selectedTicket.ticketStatus === 'resolved' ? 'bg-success' :
                                                    selectedTicket.ticketStatus === 'closed' ? 'bg-secondary' :
                                                        'bg-info'
                                                }`}>
                                                {selectedTicket.ticketStatus}
                                            </span>
                                            <span className="text-white-50">•</span>
                                            <small className="text-white-50">{selectedTicket.ticketUserEmail}</small>
                                        </div>
                                    </div>

                                    {/* Messages Area */}
                                    <div className="flex-grow-1 overflow-auto p-4">
                                        <div className="d-flex flex-column gap-3">
                                            {/* Initial Message */}
                                            <div className="chat-message-user">
                                                <div className="message-bubble user-bubble">
                                                    <div className="message-content">
                                                        {selectedTicket.message}
                                                    </div>
                                                    <div className="message-time">
                                                        {new Date(selectedTicket.createdAt).toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Conversation Messages */}
                                            {selectedTicket.adminMessage?.map((msg, index) => (
                                                <div key={index} className={`chat-message-${msg.sender === 'admin' ? 'admin' : 'user'}`}>
                                                    <div className={`message-bubble ${msg.sender === 'admin' ? 'admin-bubble' : 'user-bubble'}`}>
                                                        <div className="message-content">
                                                            {msg.message}
                                                        </div>
                                                        <div className="message-time">
                                                            {new Date(msg.timestamp).toLocaleString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div ref={messagesEndRef} />
                                        </div>
                                    </div>

                                    {/* Message Input */}
                                    <div className="p-3 border-top">
                                        <form onSubmit={handleSendMessage} className="d-flex gap-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Type your message..."
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                disabled={selectedTicket.ticketStatus === 'closed' || sending}
                                                style={{
                                                    borderRadius: '20px',
                                                    padding: '10px 20px',
                                                    border: '1px solid #e2e8f0'
                                                }}
                                            />
                                            <button
                                                type="submit"
                                                className={`btn btn-primary px-4 position-relative ${sending ? 'sending-animation' : ''}`}
                                                disabled={selectedTicket.ticketStatus === 'closed' || sending || !newMessage.trim()}
                                                style={{
                                                    borderRadius: '20px', 
                                                    background: '#2563eb',
                                                    border: 'none',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                {sending ? (
                                                    <span className="d-flex align-items-center">
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Sending...
                                                    </span>
                                                ) : (
                                                    'Send'
                                                )}
                                            </button>
                                        </form>
                                    </div>

                                    {/* CSS for button animation */}
                                    <style jsx>{`
                                        @keyframes pulse {
                                            0% { transform: scale(1); }
                                            50% { transform: scale(0.97); }
                                            100% { transform: scale(1); }
                                        }
                                        
                                        .sending-animation {
                                            animation: pulse 1.5s infinite;
                                        }
                                        
                                        .btn:not(:disabled):hover {
                                            transform: translateY(-2px);
                                            box-shadow: 0 4px 6px rgba(37, 99, 235, 0.2);
                                        }
                                        
                                        .btn:not(:disabled):active {
                                            transform: translateY(0);
                                        }
                                    `}</style>
                                </>
                            ) : (
                                <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                                    <div className="text-center">
                                        <MessageSquare size={48} className="mb-3 text-white-50" />
                                        <p className="text-white-50">Select a ticket to view the conversation</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <StyleChat />
        </div>
    );
};

export default SupportMessages;